import { get, set, del, entries } from 'idb-keyval';

import { SEVERITY_TEXT } from 'lib/opentelemetry/constants';

/**
 * Thin TTL-aware wrapper around IndexedDB (via idb-keyval) for caching rarely-changing data.
 *
 * Each entry is stored as an envelope: { data, savedAt, version, ...meta }. Keeping all storage
 * access behind this module means we can later swap the underlying mechanism (e.g. redux-persist)
 * without touching callers.
 */

/**
 * Cache schema version. Bump this whenever the shape of cached data changes — entries written by an
 * older version are then treated as a miss, so stale/incompatible data is never returned.
 */
export const CACHE_VERSION = 1;

/**
 * Reports a failed IndexedDB cache operation to OpenTelemetry. Cache access is best-effort, so the
 * operation still degrades gracefully — this only makes the failure observable in production
 * (e.g. a quota-exceeded write, or a cleanup that can't run, which would otherwise fill up storage).
 *
 * @param {string} operation - Which cache operation failed ('write' | 'drop' | 'list').
 * @param {Error} error - The caught error.
 * @param {string} [key] - The affected cache key, when applicable.
 * @returns {void}
 */
const logCacheError = (operation, error, key) => {
    try {
        // Lazy require: importing the OT logger eagerly would pull its module-load side effect
        // (`new OTLLogs()` → `diag.setLogger`) into the store's import chain. It is only needed when
        // an operation actually fails, and by then the logger is already initialized in the running app.
        const { log } = require('lib/opentelemetry/logging');

        log(`IndexedDB cache "${operation}" failed`, SEVERITY_TEXT.ERROR, {
            operation,
            key,
            error: error?.message ?? String(error),
        });
    } catch {
        // Logging is best-effort — never let an observability failure surface to the caller.
    }
};

/**
 * Reads a cached entry and reports whether it is stale.
 *
 * Any extra metadata stored alongside the envelope (passed via `writeCache`'s `meta`) is returned at
 * the top level next to `data`/`isStale`.
 *
 * @param {string} key - The cache key.
 * @param {number} ttlMs - How long the entry stays fresh, in milliseconds.
 * @returns {Promise<{data: *, isStale: boolean} | null>} The cached data with its staleness flag and
 *   any stored metadata, or null when there is no (compatible) entry. A read failure also resolves to null.
 */
export const readCache = async (key, ttlMs) => {
    try {
        const entry = await get(key);
        const { data, savedAt, version, ...rest } = entry || {};
        if (!entry || version !== CACHE_VERSION) {
            return null;
        }

        return { data, isStale: Date.now() - savedAt > ttlMs, ...rest };
    } catch {
        return null;
    }
};

/**
 * Writes data to the cache, stamping it with the current time and schema version.
 *
 * Additional top-level metadata (e.g. lastAccessedAt) can be supplied via `meta` and is spread into
 * the envelope alongside `savedAt`/`version`.
 *
 * @param {string} key - The cache key.
 * @param {*} data - The data to cache.
 * @param {Object} [meta] - Extra top-level fields to store in the envelope.
 * @returns {Promise<void>} Resolves once written (write failures are swallowed — caching is best-effort).
 */
export const writeCache = async (key, data, meta = {}) => {
    try {
        await set(key, { data, savedAt: Date.now(), version: CACHE_VERSION, ...meta });
    } catch (error) {
        // Best-effort cache: ignore quota/availability errors, but surface them for observability.
        logCacheError('write', error, key);
    }
};

/**
 * Removes a cached entry.
 *
 * @param {string} key - The cache key.
 * @returns {Promise<void>}
 */
export const dropCache = async (key) => {
    try {
        await del(key);
    } catch (error) {
        logCacheError('drop', error, key);
    }
};

/**
 * Lists every cached entry as [key, envelope] pairs. Used by retention/LRU sweeps that need to
 * inspect all buckets. A read failure resolves to an empty list.
 *
 * @returns {Promise<Array<[IDBValidKey, Object]>>}
 */
export const listCacheEntries = async () => {
    try {
        return await entries();
    } catch (error) {
        logCacheError('list', error);

        return [];
    }
};
